import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, LayoutDashboard, UserCircle, FileBarChart, Map, Users, Network, Settings, Calendar, AlertTriangle , Megaphone} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import UniversalBottomNav from './UniversalBottomNav';
import BottomNav from '../BottomNav';
import { preloadHandlers } from '../../utils/routePreload';

const ALL_SIDE_ITEMS = [
  { id: 'accueil', label: 'Accueil', icon: Home, path: '/' },
  { id: 'dashboard', label: 'Vue Globale', icon: LayoutDashboard, path: '/dg/dashboard' },
  { id: 'members', label: 'Annuaire', icon: UserCircle, path: '/dg/members' },
  { id: 'my-rdv', label: 'Mes RDV', icon: Calendar, path: '/leader/rdv' },
  { id: 'alertes-suivi', label: 'Alertes de suivi', icon: AlertTriangle, path: '/alertes-suivi' },
  { id: 'signalements', label: 'Signalements', icon: Megaphone, path: '/dg/signalements' },
  { id: 'reports', label: 'Rapports', icon: FileBarChart, path: '/dg/reports' },
  { id: 'sectors', label: 'Secteurs', icon: Map, path: '/dg/sectors' },
  { id: 'leaders', label: 'Leaders', icon: Users, path: '/dg/leaders' },
  { id: 'organisation', label: 'Organisation', icon: Network, path: '/dg/organisation' },
  { id: 'settings', label: 'Paramètres', icon: Settings, path: '/dg/settings' },
];

const DGNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const displayName = user?.displayName
    || [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
    || 'PCC Admin';

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path === '/dg/dashboard') {
      return location.pathname === '/dg/dashboard' || location.pathname === '/dg';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Bottom Navigation - Mobile Only — role-aware via BottomNav
          (never hardcode role here; a DG who ends up on a '/member/*' page
          should still see their DG navbar, not the member one).
          Hidden on md+ so it doesn't double up with the desktop sidebar. */}
      <div className="md:hidden">
        <BottomNav />
      </div>

      {/* Side Navigation - Tablet/Desktop Only */}
      <aside
        className={`hidden md:flex fixed left-0 top-0 bottom-0 w-64 flex-col border-r z-40 ${
          isDark ? 'bg-[#2D2D2D] border-[#444444]' : 'bg-white border-gray-200'
        }`}
        data-testid="dg-side-nav"
      >
        {/* Header */}
        <div className={`p-6 border-b ${isDark ? 'border-[#444444]' : 'border-gray-100'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#9398DE] to-[#7B80C9] flex items-center justify-center">
              <LayoutDashboard size={20} className="text-white" />
            </div>
            <div>
              <h1
                className={`font-bold text-lg truncate ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}
                data-testid="dg-sidebar-user-name"
                title={displayName}
              >
                {displayName}
              </h1>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Direction Générale
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 p-4 space-y-1">
          {ALL_SIDE_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                {...preloadHandlers(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  active
                    ? isDark
                      ? 'bg-[#9398DE]/20 text-[#9398DE]'
                      : 'bg-[#9398DE]/10 text-[#9398DE]'
                    : isDark
                      ? 'text-gray-400 hover:bg-[#333333] hover:text-[#F2F1EB]'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                <span className={`font-medium ${active ? 'font-semibold' : ''}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className={`p-4 border-t ${isDark ? 'border-[#444444]' : 'border-gray-100'}`}>
          <div className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
            Paris Centre Chrétien
          </div>
          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-300'}`}>
            Tableau de bord DG
          </div>
        </div>
      </aside>
    </>
  );
};

export default DGNavigation;
